import { isEmpty } from 'lodash';
import React, { FC, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { IconButton, Typography } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import { userAuthService } from '@project/services';
import { useOtpResend } from '@project/hooks';
import { Button, FormTextField } from '@project/components';
import { BaseFormProps, ErrorObject, OtpProps, ResendOtpProps } from '@project/types';
import { OtpValidation } from '@project/validators';

export const OtpForm: FC<BaseFormProps<OtpProps>> = ({ initialValues, submitForm, onSuccess }) => {
  const { isResending, resendMessage, countdown, handleResendOtp } = useOtpResend({
    resendFunction: userAuthService.resendPasswordOtp,
    createResendData: (phoneNumber: string): ResendOtpProps => ({
      phoneNumber,
    }),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const _handleFormSubmitError = (error: ErrorObject, actions: FormikHelpers<OtpProps>) => {
    actions.setSubmitting(false);
    const apiErrors = error.errors;
    if (!isEmpty(apiErrors)) {
      if (apiErrors.accountVerificationIncomplete) {
        actions.setFieldError('phoneNumber', apiErrors.accountVerificationIncomplete);
        actions.setFieldError('password', apiErrors.password);
        actions.setFieldError('confirmPassword', apiErrors.confirmPassword);
        actions.setFieldError('otp', apiErrors.Otp);
        actions.setFieldError('email', apiErrors.email);
      } else actions.setFieldError('phoneNumber', apiErrors.phoneNumber);
    } else {
      actions.setFieldError('phoneNumber', error.message);
    }
  };

  const _handleSubmission = (formData: OtpProps, actions: FormikHelpers<OtpProps>) => {
    submitForm(formData)
      .then(() => {
        actions.setSubmitting(false);
        onSuccess();
      })
      .catch((error: ErrorObject) => {
        _handleFormSubmitError(error, actions);
      });
  };

  const _toggleShowPassword = () => setShowPassword(!showPassword);
  const _toggleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const passwordButtonIcon = showPassword ? <Visibility /> : <VisibilityOff />;
  const confirmPasswordButtonIcon = showConfirmPassword ? <Visibility /> : <VisibilityOff />;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={OtpValidation}
      validateOnBlur
      validateOnChange={false}
      onSubmit={_handleSubmission}
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleReset,
      }) => (
        <form onReset={handleReset} onSubmit={handleSubmit} className="auth-form-body">
          <p className="auth-heading">Reset Password</p>
          <p className="auth-sub-heading">Enter the OTP and reset your Password.</p>
          <Typography className="self-start ml-20 text-xs">{`*******${values.phoneNumber.slice(
            -3,
          )}`}</Typography>
          <div className="w-4/5">
            <FormTextField
              variableName="otp"
              placeholder="OTP Number"
              className="w-full self-center"
              values={values}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched}
              errors={errors}
              icon={<img src="assets/icons/lock-icon.png" alt="mail" />}
            />
            <div className="w-full flex justify-start">
              {countdown > 0 ? (
                <span className="text-sm text-gray-500">Resend OTP in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleResendOtp(values.phoneNumber)}
                  disabled={isResending || isSubmitting}
                  className={`text-sm underline ${
                    isResending || isSubmitting
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-600 hover:text-blue-800 cursor-pointer'
                  }`}
                >
                  {isResending ? 'Resending...' : 'Resend OTP'}
                </button>
              )}
            </div>
            {resendMessage && (
              <p
                className={`text-sm mt-2 ${
                  resendMessage.includes('Failed') ? 'text-red-500' : 'text-green-500'
                }`}
              >
                {resendMessage}
              </p>
            )}
            <Typography className="text-gray-500 font-light mb-1 mt-5 text-xs">
              Optional email field for immediate login after OTP confirmation
            </Typography>
            <FormTextField
              variableName="email"
              placeholder="Email"
              className="w-full self-center"
              values={values}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched}
              errors={errors}
            />
          </div>
          <div className="w-4/5">
            <Typography className="text-gray-500 font-light mb-1 text-xs">
              Password must be at least 6 characters long
            </Typography>
            <FormTextField
              variableName="password"
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              values={values}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched}
              errors={errors}
              icon={<img src="assets/icons/lock-icon.png" alt="password" />}
              endIcon={
                <IconButton size="small" onClick={_toggleShowPassword}>
                  {passwordButtonIcon}
                </IconButton>
              }
              required
              className="w-full self-center"
            />
          </div>
          <FormTextField
            variableName="confirmPassword"
            placeholder="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            values={values}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched}
            errors={errors}
            icon={<img src="assets/icons/lock-icon.png" alt="passwordConfirm" />}
            endIcon={
              <IconButton size="small" onClick={_toggleShowConfirmPassword}>
                {confirmPasswordButtonIcon}
              </IconButton>
            }
            required
            className="w-4/5 self-center"
          />
          <Button
            isLoading={isSubmitting}
            onClick={handleSubmit}
            className="auth-form-submit-button"
          >
            Reset Password
          </Button>
        </form>
      )}
    </Formik>
  );
};
